import React, { useCallback, useEffect, useRef, useState } from "react";
import "../../styles/CSS/Portfolio.css";
import ScrollArrow from "../../Components/ScrollArrow/ScrollArrow.js";
import PortfolioItem, {
  ModalPortfolioItem,
} from "../../Components/PortfolioItem/PortfolioItem.jsx";
import PortfolioItems from "./PortfolioItems.js";
import {
  faDownLeftAndUpRightToCenter,
  faUpRightAndDownLeftFromCenter,
  faTimeline,
} from "@fortawesome/free-solid-svg-icons";
import FontAwesomeTextIcon from "../../Components/FontAwesomeTextIcon/index.jsx";
import ColorCharacters from "../../Components/GlowingText/ColorCharacters.jsx";
import HorizontalTimeline from "../../Components/HorizontalTimeline/index.jsx";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { IconButton, Tooltip } from "@mui/material";
import VerticalTimeline from "../../Components/VerticalTimeline/index.jsx";
import { isMobile } from "mobile-device-detect";
import { isMobileClient } from "../../util/misc.js";
import {
  trackPortfolioProjectOpen,
  trackPortfolioProjectLinkClick,
  trackPortfolioModalNavigate,
} from "../../util/analytics/portfolio.js";
import { trackButtonClick, trackNavClick } from "../../util/analytics/ui.js";

export default ({ color, onModalToggled, modalOpen }) => {
  const [state, setState] = useState({
    showingModal: false,
    modalItem: null,
    hovered: false,
    isCondensed: false,
  });

  const [view, setView] = useState("timeline");
  const [timelineSubView, setTimelineSubView] = useState("vertical");

  const containerRef = useRef(null);
  const [sContainerRef, setSContainerRef] = useState(null);

  useEffect(() => {
    setSContainerRef(containerRef.current);
  }, []);

  useEffect(() => {
    if (
      modalOpen !== undefined &&
      !modalOpen &&
      modalOpen !== state.showingModal
    ) {
      setState((s) => ({ ...s, showingModal: modalOpen }));
    }
  }, [modalOpen]);

  useEffect(() => {
    if (onModalToggled) {
      onModalToggled(state.showingModal);
    }
  }, [onModalToggled, state.showingModal]);

  useEffect(() => {
    if (isMobile || isMobileClient()) {
      setView("cards");
    }
  }, []);

  const getProjectPosition = useCallback((item) => {
    return PortfolioItems.findIndex((candidate) => candidate === item);
  }, []);

  const getAnalyticsContext = useCallback(
    (item, surface, overrides = {}) => {
      return {
        projectPosition: getProjectPosition(item),
        viewMode: view,
        surface,
        isCondensed: state.isCondensed,
        ...overrides,
      };
    },
    [getProjectPosition, state.isCondensed, view]
  );

  const openPortfolioItem = useCallback(
    (item, surface) => {
      trackPortfolioProjectOpen(item, getAnalyticsContext(item, surface));

      setState((currentState) => ({
        ...currentState,
        showingModal: true,
        modalItem: item,
      }));
    },
    [getAnalyticsContext]
  );

  const closePortfolioModal = useCallback(() => {
    setState((currentState) => ({
      ...currentState,
      showingModal: false,
      modalItem: null,
    }));
  }, []);

  const handleProjectLinkClick = useCallback(
    (item, link, linkIndex, surface = "card_link") => {
      trackPortfolioProjectLinkClick(
        item,
        link,
        getAnalyticsContext(item, surface, {
          linkIndex,
        })
      );
    },
    [getAnalyticsContext]
  );

  const handleModalNavigate = useCallback(
    (direction) => {
      if (!state.showingModal || !state.modalItem) return;

      const currentIndex = PortfolioItems.indexOf(state.modalItem);
      let nextIndex = currentIndex + direction;

      if (nextIndex < 0) nextIndex = PortfolioItems.length - 1;
      if (nextIndex >= PortfolioItems.length) nextIndex = 0;

      const nextItem = PortfolioItems[nextIndex];

      trackPortfolioModalNavigate(
        state.modalItem,
        nextItem,
        direction,
        getAnalyticsContext(nextItem, "modal_navigation", {
          projectPosition: nextIndex,
        })
      );

      trackButtonClick("navigate_portfolio_modal", {
        surface: "portfolio_modal",
        context: direction > 0 ? "next_button" : "previous_button",
        project: state.modalItem.title ? state.modalItem.title : "unknown",
      });

      setState((currentState) => ({
        ...currentState,
        modalItem: nextItem,
      }));
    },
    [getAnalyticsContext, state.modalItem, state.showingModal]
  );

  return (
    <>
      <ModalPortfolioItem
        color={color}
        item={state.modalItem}
        items={PortfolioItems}
        open={state.showingModal}
        onNavigate={handleModalNavigate}
        onLinkClick={(item, link, linkIndex) => {
          handleProjectLinkClick(item, link, linkIndex, "modal_link");
        }}
        handleClose={closePortfolioModal}
      />

      <div className="portfolio">
        <div className="content-grid">
          <div className="title">
            <div className="blur-container">
              <ColorCharacters
                style={{
                  transition: !state.hovered
                    ? "color 0s linear"
                    : "color .15s linear 0s",
                  fontSize: "3rem",
                }}
                string={"Portfolio"}
              />
            </div>

            <div
              className="blur-container"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {view === "cards" && (
                <IconButton
                  onClick={() => {
                    setState((s) => ({
                      ...s,
                      isCondensed: !s.isCondensed,
                    }))
                    trackButtonClick(state.isCondensed ? "expand_all_projects" : "shrink_all_projects", {
                      surface: "portfolio_page",
                      context: state.isCondensed ? "expand_all_button" : "shrink_all_button",
                    })
                  }}
                >
                  <FontAwesomeTextIcon
                    icon={
                      state.isCondensed
                        ? faUpRightAndDownLeftFromCenter
                        : faDownLeftAndUpRightToCenter
                    }
                    text={state.isCondensed ? "Expand" : "Shrink"}
                  />
                </IconButton>
              )}

              {!isMobile && !isMobileClient() && (
                <IconButton
                  sx={{ p: 0, m: 1 }}
                  onClick={() => {
                    setView(view === "timeline" ? "cards" : "timeline");
                    trackButtonClick(view === "timeline" ? "switch_to_cards_view" : "switch_to_timeline_view", {
                      surface: "portfolio_page",
                      context: "view_toggle_button",
                    });
                  }}
                >
                  {view === "timeline" ? (
                    <Tooltip title="Cards" placement="bottom">
                      <DashboardIcon />
                    </Tooltip>
                  ) : (
                    <FontAwesomeTextIcon icon={faTimeline} text={"Timeline"} />
                  )}
                </IconButton>
              )}
            </div>
          </div>

          <div
            className="portfolio-wrapper"
            style={{
              justifyContent: view === "cards" ? "center" : "start",
              padding: view === "timeline" ? "0" : "0 3em",
              overflowX: view === "timeline" ? "auto" : "hidden",
            }}
            ref={containerRef}
          >
            {view === "cards" &&
              PortfolioItems.map((item, i) => (
                <PortfolioItem
                  key={"portfolio-item-" + i}
                  item={item}
                  isCondensed={state.isCondensed}
                  onClick={() => {
                    openPortfolioItem(item, "card");
                  }}
                  onShowMore={() => {
                    openPortfolioItem(item, "show_more");
                  }}
                  onShowLess={closePortfolioModal}
                  onLinkClick={(link, linkIndex) => {
                    handleProjectLinkClick(item, link, linkIndex, "card_link");
                  }}
                />
              ))}

            {view === "timeline" && (
              <>
                {timelineSubView === "horizontal" && (
                  <HorizontalTimeline
                    container={sContainerRef}
                    onShowMore={(item) => {
                      openPortfolioItem(item, "timeline");
                    }}
                  />
                )}

                {timelineSubView === "vertical" && (
                  <VerticalTimeline
                    items={PortfolioItems}
                    onShowMore={(item) => {
                      openPortfolioItem(item, "timeline");
                    }}
                  />
                )}
              </>
            )}
          </div>
        </div>

        <ScrollArrow to=".about" onClick={() => {
          trackButtonClick("scroll_to_about", {
            surface: "portfolio_page",
            context: "scroll_arrow",
          });
          trackNavClick("about", {
            navLabel: "Scroll Arrow to About",
            navGroup: "portfolio_scroll_arrow",
          });
        }} />
      </div>
    </>
  );
};